/**
 * Delivery Estimate Calculator
 * Calculates delivery date based on order time and business days
 * Rules:
 * - Order before 12pm on a business day: Delivery in 2 business days
 * - Order after 12pm on a business day: Delivery in 3 business days
 * - Order on weekend: Ships Monday at 12pm, so delivery Wednesday (2 business days from Monday)
 */

(function() {
  'use strict';

  /**
   * Get current date and time in Paris timezone
   * @returns {Date} - Current date/time in Paris
   */
  function getParisDateTime() {
    // DEBUG MODE: Uncomment to test with specific date/time
    // return new Date('2025-11-06T16:00:00'); // Jeudi 6 novembre 2025 à 16h (après-midi)

    const now = new Date();
    // Convert to Paris time using toLocaleString
    const parisTimeString = now.toLocaleString('en-US', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    return new Date(parisTimeString);
  }

  /**
   * Calculate the shipping start date (when order is processed)
   * @returns {Object} - {date: Date, hour: number} - Shipping start date and hour
   */
  function getShippingStartDate() {
    const parisNow = getParisDateTime();
    const currentHour = parisNow.getHours();
    const currentDay = parisNow.getDay(); // 0=Sunday, 6=Saturday

    let shippingDate = new Date(parisNow);
    let shippingHour = 12;

    // If it's Saturday (6) or Sunday (0), shipping starts Monday at 12pm
    if (currentDay === 0) {
      // Sunday -> ship Monday
      shippingDate.setDate(shippingDate.getDate() + 1);
    } else if (currentDay === 6) {
      // Saturday -> ship Monday
      shippingDate.setDate(shippingDate.getDate() + 2);
    } else {
      // Business day (Monday-Friday)
      if (currentHour >= 12) {
        // After 12pm -> ship tomorrow at 12pm
        shippingDate.setDate(shippingDate.getDate() + 1);
        // If tomorrow is Saturday, ship Monday
        if (shippingDate.getDay() === 6) {
          shippingDate.setDate(shippingDate.getDate() + 2);
        }
        // If tomorrow is Sunday, ship Monday
        if (shippingDate.getDay() === 0) {
          shippingDate.setDate(shippingDate.getDate() + 1);
        }
      }
      // Before 12pm -> ship today at 12pm (already set)
    }

    return { date: shippingDate, hour: shippingHour };
  }

  /**
   * Calculate Easter date for a given year (using Computus algorithm)
   * @param {number} year - Year
   * @returns {Date} - Easter Sunday date
   */
  function getEasterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  /**
   * Check if a date is a French public holiday
   * @param {Date} date - Date to check
   * @returns {boolean} - True if it's a public holiday
   */
  function isFrenchPublicHoliday(date) {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();

    // Fixed holidays
    const fixedHolidays = [
      { month: 0, day: 1 },   // 1er janvier - Jour de l'an
      { month: 4, day: 1 },   // 1er mai - Fête du travail
      { month: 4, day: 8 },   // 8 mai - Victoire 1945
      { month: 6, day: 14 },  // 14 juillet - Fête nationale
      { month: 7, day: 15 },  // 15 août - Assomption
      { month: 10, day: 1 },  // 1er novembre - Toussaint
      { month: 10, day: 11 }, // 11 novembre - Armistice 1918
      { month: 11, day: 25 }  // 25 décembre - Noël
    ];

    // Check fixed holidays
    for (const holiday of fixedHolidays) {
      if (month === holiday.month && day === holiday.day) {
        return true;
      }
    }

    // Calculate mobile holidays based on Easter
    const easter = getEasterDate(year);

    // Lundi de Pâques (Easter Monday) = Easter + 1 day
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);

    // Ascension (39 days after Easter, always a Thursday)
    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);

    // Lundi de Pentecôte (Whit Monday) = Easter + 50 days
    const whitMonday = new Date(easter);
    whitMonday.setDate(easter.getDate() + 50);

    // Check if date matches any mobile holiday
    const dateString = `${year}-${month}-${day}`;
    const easterMondayString = `${easterMonday.getFullYear()}-${easterMonday.getMonth()}-${easterMonday.getDate()}`;
    const ascensionString = `${ascension.getFullYear()}-${ascension.getMonth()}-${ascension.getDate()}`;
    const whitMondayString = `${whitMonday.getFullYear()}-${whitMonday.getMonth()}-${whitMonday.getDate()}`;

    return dateString === easterMondayString ||
           dateString === ascensionString ||
           dateString === whitMondayString;
  }

  /**
   * Calculate delivery date by adding business days from shipping start
   * @param {Date} startDate - Starting date
   * @param {number} businessDays - Number of business days to add
   * @returns {Date} - Calculated delivery date
   */
  function addBusinessDays(startDate, businessDays) {
    let currentDate = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < businessDays) {
      currentDate.setDate(currentDate.getDate() + 1);
      let dayOfWeek = currentDate.getDay();

      // If it's not Saturday (6) or Sunday (0), and not a public holiday, count it
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isFrenchPublicHoliday(currentDate)) {
        daysAdded++;
      }
    }

    return currentDate;
  }

  /**
   * Calculate final delivery date
   * @returns {Date} - Delivery date
   */
  function calculateDeliveryDate() {
    const parisNow = getParisDateTime();
    const currentHour = parisNow.getHours();
    const currentDay = parisNow.getDay();

    const shippingStart = getShippingStartDate();

    // Toujours 2 jours ouvrés à partir de la date d'expédition
    // Si commande avant 12h jour ouvré : expédition aujourd'hui → livraison J+2
    // Si commande après 12h jour ouvré : expédition demain → livraison J+3 (demain + 2)
    // Si commande week-end : expédition lundi → livraison mercredi (lundi + 2)
    const deliveryDays = 2;

    return addBusinessDays(shippingStart.date, deliveryDays);
  }

  /**
   * Format date in French format
   * @param {Date} date - Date to format
   * @returns {string} - Formatted date string
   */
  function formatDateFrench(date) {
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];

    return `${dayName} ${day} ${month}`;
  }

  /**
   * Initialize delivery estimate display
   */
  function initDeliveryEstimate() {
    const deliveryDateElement = document.querySelector('[data-delivery-date]');

    if (!deliveryDateElement) {
      return;
    }

    // Calculate delivery date
    const deliveryDate = calculateDeliveryDate();

    // Format and display the date
    const formattedDate = formatDateFrench(deliveryDate);
    deliveryDateElement.textContent = formattedDate;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeliveryEstimate);
  } else {
    initDeliveryEstimate();
  }
})();
